"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.primarySupportingSource = exports.keyPointSpeakerName = exports.COLLAPSED_KEY_POINTS = void 0;
exports.filterKeyPointsForTier = filterKeyPointsForTier;
exports.keyPointSpeaker = keyPointSpeaker;
exports.displayStanceLists = displayStanceLists;
exports.stanceSources = stanceSources;
exports.splitSentences = splitSentences;
exports.splitClaimText = splitClaimText;
/** Key points shown before the "Show all key points" fold. */
exports.COLLAPSED_KEY_POINTS = 5;
/**
 * The unfiltered detail payload mixes every tier's keypoints. The short tier
 * keeps untyped (legacy) keypoints so old `?kp=` links still resolve; other
 * tiers arrive pre-filtered from their own fetch.
 */
function filterKeyPointsForTier(keyPoints, tier) {
    if (tier !== 'short')
        return [...keyPoints];
    return keyPoints.filter((kp) => (kp.type ?? 'short') === 'short');
}
const listOf = (list) => Array.isArray(list) ? [...list] : [];
/**
 * The key point's speaker when the API names one (`primarySource`), else
 * null. A speaker without an id or a name is treated as absent, so the caller
 * falls back to today's rule.
 */
function keyPointSpeaker(keyPoint) {
    const speaker = keyPoint.primarySource;
    return speaker && speaker.id && speaker.name ? speaker : null;
}
/**
 * The For / Neutral / Against lists to display. When the key point names its
 * speaker, the speaker chip already shows them, so they are left out of For and
 * Neutral. Against is never filtered: a speaker the model listed against their
 * own point stays there, which is how the page already shows a dispute. With
 * no named speaker the lists are returned exactly as served.
 */
function displayStanceLists(keyPoint) {
    const speaker = keyPointSpeaker(keyPoint);
    const notSpeaker = (s) => !speaker || s.id !== speaker.id;
    return {
        sourcesFor: listOf(keyPoint.sourcesFor).filter(notSpeaker),
        sourcesNeutral: listOf(keyPoint.sourcesNeutral).filter(notSpeaker),
        sourcesAgainst: listOf(keyPoint.sourcesAgainst),
    };
}
/**
 * For, then Neutral, then Against: the website's order. The speaker is left
 * out of For and Neutral when the key point names one (see
 * `displayStanceLists`).
 */
function stanceSources(keyPoint) {
    const lists = displayStanceLists(keyPoint);
    const tag = (list, stance) => list.map((s) => ({ id: s.id, name: s.name, stance }));
    return [
        ...tag(lists.sourcesFor, 'For'),
        ...tag(lists.sourcesNeutral, 'Neutral'),
        ...tag(lists.sourcesAgainst, 'Against'),
    ];
}
/**
 * The name on the chip beside "Impact" on the phone layouts: the key point's
 * speaker when the API names one, else the first supporting source (today's
 * rule), else nothing.
 */
const keyPointSpeakerName = (keyPoint) => keyPointSpeaker(keyPoint)?.name ?? listOf(keyPoint.sourcesFor)[0]?.name ?? '';
exports.keyPointSpeakerName = keyPointSpeakerName;
/**
 * The first supporting source in an already built stance list, if any.
 * It cannot see `primarySource`, and `stanceSources` drops the speaker from
 * For, so use `keyPointSpeakerName` for the chip.
 */
const primarySupportingSource = (sources) => (sources[0]?.stance === 'For' ? sources[0].name : '');
exports.primarySupportingSource = primarySupportingSource;
const ABBREVIATIONS = new Set([
    'dr',
    'mr',
    'mrs',
    'ms',
    'prof',
    'sr',
    'jr',
    'vs',
    'etc',
    'i.e',
    'e.g',
    'a.m',
    'p.m',
    'am',
    'pm',
    'u.s',
    'u.k',
    'uk',
    'us',
    'inc',
    'ltd',
    'corp',
    'co',
    'st',
    'ave',
    'blvd',
    'no',
    'vol',
    'pp',
    'ed',
    'ph.d',
    'phd',
    'm.d',
    'b.a',
    'b.s',
    'm.a',
    'm.s',
]);
// Checks the letters before the period ("Dr") and, for dotted forms such as
// "e.g." or "U.S.", the letters and periods before it ("e.g", "u.s").
const isAbbreviationAt = (text, periodIndex) => {
    const before = text.substring(0, periodIndex);
    const word = /[a-zA-Z]*$/.exec(before)?.[0] ?? '';
    const dotted = /[a-zA-Z.]*$/.exec(before)?.[0] ?? '';
    return (ABBREVIATIONS.has(word.toLowerCase()) ||
        ABBREVIATIONS.has(dotted.toLowerCase()));
};
/** Abbreviation-aware sentence split, as the website's key point body does. */
function splitSentences(text) {
    const sentences = [];
    let lastIndex = 0;
    let i = 0;
    while (i < text.length) {
        const char = text[i];
        if (char === '.' || char === '!' || char === '?') {
            const isEnd = i === text.length - 1;
            // The WHOLE whitespace run, not one character. Two spaces after a full
            // stop, or a newline plus indentation, are ordinary in pasted prose, and
            // looking only at `i + 2` read them as mid-sentence — so the claim was
            // returned as a single sentence and the lead/rest split never happened.
            let afterIndex = i + 1;
            while (afterIndex < text.length && /\s/.test(text[afterIndex] ?? '')) {
                afterIndex++;
            }
            const hasSpaceAfter = afterIndex > i + 1;
            const afterSpace = text[afterIndex];
            const capitalFollows = hasSpaceAfter && afterSpace !== undefined && /[A-Z]/.test(afterSpace);
            const isSentenceEnd = (isEnd || capitalFollows || char === '!' || char === '?') &&
                !(char === '.' && isAbbreviationAt(text, i));
            if (isSentenceEnd) {
                const sentence = text.substring(lastIndex, i + 1).trim();
                if (sentence)
                    sentences.push(sentence);
                lastIndex = i + 1;
                while (lastIndex < text.length && /\s/.test(text[lastIndex] ?? '')) {
                    lastIndex++;
                }
                i = lastIndex;
                continue;
            }
        }
        i++;
    }
    const remaining = text.substring(lastIndex).trim();
    if (remaining)
        sentences.push(remaining);
    return sentences;
}
/**
 * The key point body is laid out as its first sentence, a blank line, then
 * the rest. Presentation only: the text itself is never altered.
 */
function splitClaimText(claimText) {
    const text = claimText ?? '';
    const sentences = splitSentences(text);
    if (sentences.length < 2)
        return { lead: text, rest: null };
    return {
        lead: sentences[0] ?? '',
        rest: sentences.slice(1).join(' '),
    };
}
